import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Building2, 
  Wind, 
  Snowflake, 
  Flame, 
  Zap, 
  Lightbulb, 
  ArrowUpDown, 
  AlertTriangle, 
  Camera, 
  KeyRound, 
  Droplet,
  Search,
  Wrench,
  Calendar,
  DollarSign
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { 
  assets, 
  areas,
  buildings, 
  assetCategories,
  getAssetById,
  getBuildingById,
  getCategoryById 
} from '@/data/mockData';
import { getMergedWorkOrders, getMergedFaults } from '@/data/faultStore';
import { workOrders, faults } from '@/data/mockData';

const iconMap = {
  Wind, Snowflake, Flame, Zap, Lightbulb, ArrowUpDown, AlertTriangle, Camera, KeyRound, Droplet
};

const getStatusColor = (status) => {
  const colors = {
    operational: 'success',
    maintenance: 'warning',
    critical: 'destructive',
    offline: 'destructive',
  };
  return colors[status] || 'default';
};

const AssetCard = ({ asset, onClick }) => {
  const { t } = useLanguage();
  const building = getBuildingById(asset.buildingId);
  const category = getCategoryById(asset.category);
  const IconComponent = iconMap[category?.icon];

  return (
    <Card 
      className="shadow-soft hover:shadow-elevated transition-all cursor-pointer hover:border-primary"
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary-light rounded-lg flex-shrink-0">
            {IconComponent && <IconComponent className="w-6 h-6 text-primary" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-semibold text-foreground text-base truncate">
                {asset.name}
              </h3>
              <Badge variant={getStatusColor(asset.status)}>
                {t(asset.status)}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {building?.name}
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">{t('lastMaintenance')}:</span>
                <p className="font-medium text-foreground mt-0.5">
                  {new Date(asset.lastMaintenance).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">{t('totalCost')}:</span>
                <p className="font-medium text-foreground mt-0.5">
                  {(asset.totalCost / 1000).toFixed(0)}k Kč
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const AssetsPage = () => {
  const { t } = useLanguage();
  const [selectedArea, setSelectedArea] = useState(areas.length ? areas[0].id : 'all');
  const [selectedBuilding, setSelectedBuilding] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);

  const buildingsInArea = selectedArea === 'all'
    ? buildings
    : buildings.filter(b => b.areaId === selectedArea);
  const buildingIdsInArea = new Set(buildingsInArea.map(b => b.id));

  const filteredAssets = assets.filter(asset => {
    const matchesArea = selectedArea === 'all' || buildingIdsInArea.has(asset.buildingId);
    const matchesBuilding = selectedBuilding === 'all' || asset.buildingId === selectedBuilding;
    const matchesCategory = selectedCategory === 'all' || asset.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || asset.status === selectedStatus;
    const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesArea && matchesBuilding && matchesCategory && matchesStatus && matchesSearch;
  });

  const selectedAreaName = areas.find(a => a.id === selectedArea)?.name;
  const selectedBuildingName = getBuildingById(selectedBuilding)?.name;

  const assetsByStatus = {
    operational: assets.filter(a => a.status === 'operational').length,
    maintenance: assets.filter(a => a.status === 'maintenance').length,
    critical: assets.filter(a => a.status === 'critical').length,
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('assetRegistry')}</h1>
          <p className="text-muted-foreground">
            {selectedAreaName && selectedBuildingName && selectedBuilding !== 'all'
              ? `${selectedAreaName} → ${selectedBuildingName}`
              : selectedAreaName || null}{' '}
            · {filteredAssets.length} {filteredAssets.length === 1 ? 'asset' : 'assets'}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-soft border-success border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('operational')}</p>
                <p className="text-3xl font-bold text-foreground">{assetsByStatus.operational}</p>
              </div>
              <div className="p-3 bg-success-light rounded-lg">
                <Building2 className="w-6 h-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft border-warning border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('maintenance')}</p>
                <p className="text-3xl font-bold text-foreground">{assetsByStatus.maintenance}</p>
              </div>
              <div className="p-3 bg-warning-light rounded-lg">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft border-destructive border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('critical')}</p>
                <p className="text-3xl font-bold text-foreground">{assetsByStatus.critical}</p>
              </div>
              <div className="p-3 bg-destructive-light rounded-lg">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-soft">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={selectedArea}
              onValueChange={(v) => {
                setSelectedArea(v);
                const nextBuildings = v === 'all' ? buildings : buildings.filter(b => b.areaId === v);
                const stillValid = selectedBuilding === 'all' || nextBuildings.some(b => b.id === selectedBuilding);
                if (!stillValid) setSelectedBuilding('all');
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Areál" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny areály</SelectItem>
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
              <SelectTrigger>
                <SelectValue placeholder={t('building')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('building')} - All</SelectItem>
                {buildingsInArea.map((building) => (
                  <SelectItem key={building.id} value={building.id}>
                    {building.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t('equipment')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Category - All</SelectItem>
                {assetCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('status')} - All</SelectItem>
                <SelectItem value="operational">{t('operational')}</SelectItem>
                <SelectItem value="maintenance">{t('maintenance')}</SelectItem>
                <SelectItem value="critical">{t('critical')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assets Grid */}
      {filteredAssets.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={t('noData')}
          description={selectedArea !== 'all' || selectedBuilding !== 'all' || selectedCategory !== 'all' || selectedStatus !== 'all' || searchQuery ? 'Zkuste upravit filtry nebo vyhledávání.' : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onClick={() => setSelectedAsset(asset)}
            />
          ))}
        </div>
      )}

      {/* Asset Detail Modal */}
      <Dialog open={!!selectedAsset} onOpenChange={(open) => !open && setSelectedAsset(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedAsset && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary-light flex-shrink-0">
                    {(function() {
                      const cat = getCategoryById(selectedAsset.category);
                      const Icon = cat ? iconMap[cat.icon] : null;
                      return Icon ? <Icon className="w-6 h-6 text-primary" /> : null;
                    })()}
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="text-xl">{selectedAsset.name}</DialogTitle>
                    <CardDescription>
                      {getBuildingById(selectedAsset.buildingId)?.name}
                      {getCategoryById(selectedAsset.category) && ` · ${getCategoryById(selectedAsset.category).name}`}
                    </CardDescription>
                  </div>
                  <Badge variant={getStatusColor(selectedAsset.status)} className="flex-shrink-0">
                    {t(selectedAsset.status)}
                  </Badge>
                </div>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>Instalace:</span>
                    <span className="text-foreground">{new Date(selectedAsset.installDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>Záruka do:</span>
                    <span className="text-foreground">{new Date(selectedAsset.warrantyExpiry).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>{t('lastMaintenance')}:</span>
                    <span className="text-foreground">{new Date(selectedAsset.lastMaintenance).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>Další údržba:</span>
                    <span className="text-foreground">{new Date(selectedAsset.nextMaintenance).toLocaleDateString()}</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="w-4 h-4" />
                    <span>{t('totalCost')}:</span>
                    <span className="text-foreground font-medium">{(selectedAsset.totalCost / 1000).toFixed(0)}k Kč</span>
                  </div>
                </div>
                {(() => {
                  const allWo = getMergedWorkOrders(workOrders);
                  const assetWo = allWo.filter((wo) => wo.assetId === selectedAsset.id);
                  const allF = getMergedFaults(faults);
                  const assetFaults = allF.filter((f) => f.assetId === selectedAsset.id);
                  return (
                    <>
                      {assetWo.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                            <Wrench className="w-4 h-4" />
                            {t('workOrdersSection')} ({assetWo.length})
                          </h4>
                          <ul className="space-y-2">
                            {assetWo.slice(0, 5).map((wo) => (
                              <li key={wo.id} className="text-sm p-2 rounded-md bg-muted/50">
                                <span className="font-medium text-foreground">{wo.title}</span>
                                <span className="text-muted-foreground ml-2">
                                  {t(wo.status)} · {new Date(wo.dueDate).toLocaleDateString()}
                                </span>
                              </li>
                            ))}
                            {assetWo.length > 5 && (
                              <li className="text-xs text-muted-foreground">{t('moreItems').replace('{count}', assetWo.length - 5)}</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {assetFaults.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            {t('reportedFaultsSection')} ({assetFaults.length})
                          </h4>
                          <ul className="space-y-2">
                            {assetFaults.slice(0, 3).map((f) => (
                              <li key={f.id} className="text-sm p-2 rounded-md bg-destructive/10 text-foreground">
                                {f.description}
                                <span className="text-muted-foreground text-xs ml-2">{f.status}</span>
                              </li>
                            ))}
                            {assetFaults.length > 3 && (
                              <li className="text-xs text-muted-foreground">{t('moreItems').replace('{count}', assetFaults.length - 3)}</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {assetWo.length === 0 && assetFaults.length === 0 && (
                        <p className="text-sm text-muted-foreground">{t('noOrdersOrFaults')}</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
